import { cn } from 'cn';

import {
  TOTAL_ETAPAS_REGISTRO,
  contarEtapasConcluidas,
  isFalta,
  statusRegistro,
  type MateriaOutputType,
  type ProfessorOutputType,
  type RegistroResumoOutputType,
} from '@shared/dto';

import { SegmentedProgress } from '../../../components/common/segmented-progress';
import { corDaMateria } from '../../../components/common/materia-cores';
import { StatusRegistroBadge } from '../../../components/common/registro-form/status-badge';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../hooks/use-auth';
import { linhasPreview } from '../../../lib/registro-emojis';

function formatarHora(valor: Date | string | null): string {
  if (!valor) {
    return '';
  }
  const data = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(data.getTime())) {
    return '';
  }
  return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

const LABEL_BOTAO = {
  NAO_INICIADO: 'Registrar aula',
  PENDENTE: 'Continuar',
  CONCLUIDO: 'Ver acompanhamento',
} as const;

export function RegistroRow({
  registro,
  bloqueadoFuturo,
  professores,
  materias,
  onRegistrarAula,
  onVerHistorico,
}: {
  registro: RegistroResumoOutputType;
  /** `true` bloqueia só a escrita ("Registrar aula"/"Continuar") -- "Ver acompanhamento" (leitura) continua sempre liberado. */
  bloqueadoFuturo: boolean;
  professores: ProfessorOutputType[];
  materias: MateriaOutputType[];
  onRegistrarAula: () => void;
  onVerHistorico: () => void;
}) {
  const { podeEscreverRegistro } = useAuth();
  const status = statusRegistro(registro);
  const concluido = status === 'CONCLUIDO';
  const somenteLeitura = concluido || !podeEscreverRegistro(registro.professorId);
  const rotuloBotao = somenteLeitura ? LABEL_BOTAO.CONCLUIDO : LABEL_BOTAO[status];
  const materiaNome = materias.find((m) => m.id === registro.materiaId)?.nome ?? '—';
  const professorNome = professores.find((p) => p.id === registro.professorId)?.nome ?? '—';
  const etapasConcluidas = contarEtapasConcluidas(registro);
  const falta = isFalta(registro);
  const linhas = linhasPreview(registro);

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start gap-3">
        <div className="flex shrink-0 items-start pt-0.5">
          <span
            className="grid size-7 place-items-center rounded-lg text-xs font-bold text-white"
            style={{ background: corDaMateria(registro.materiaId, materias) }}
          >
            {materiaNome.charAt(0).toUpperCase()}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-semibold text-foreground">{registro.alunoNome}</h3>
            <StatusRegistroBadge status={status} />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {materiaNome} · {professorNome}
            {registro.estagio ? ` · ${registro.estagio}` : ''}
          </p>

          {concluido ? (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Concluído · {formatarHora(registro.atualizadoEm)}
              {!falta ? ' · Relatório enviado' : null}
            </p>
          ) : status === 'PENDENTE' ? (
            <div className="mt-2">
              <SegmentedProgress total={TOTAL_ETAPAS_REGISTRO} preenchido={etapasConcluidas} />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Última atualização: {formatarHora(registro.atualizadoEm)}
              </p>
            </div>
          ) : null}

          {linhas.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {linhas.map((linha, indice) => (
                <span
                  key={indice}
                  className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600"
                >
                  {linha.topicoEmoji} {linha.valorEmoji} {linha.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="shrink-0">
          <Button
            variant={somenteLeitura ? 'outline' : 'default'}
            size="sm"
            className={cn('rounded-xl text-xs')}
            onClick={somenteLeitura ? onVerHistorico : onRegistrarAula}
            disabled={!somenteLeitura && bloqueadoFuturo}
          >
            {rotuloBotao}
          </Button>
        </div>
      </div>
    </div>
  );
}
